import { Car, Users, Clock, Shield, Send, CheckCircle } from 'lucide-react';
import { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import { Helmet } from 'react-helmet';

export default function QuotationForm() {
    const formRef = useRef();
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);

    const [formValues, setFormValues] = useState({
        full_name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        requirements: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormValues((prev) => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors((prev) => {
                const updated = { ...prev };
                delete updated[name];
                return updated;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formValues.full_name.trim()) newErrors.full_name = 'Please enter your full name';
        if (!formValues.email.trim()) newErrors.email = 'Please enter your email';
        if (!formValues.phone.trim()) newErrors.phone = 'Please enter your phone number';
        if (!formValues.service.trim()) newErrors.service = 'Please select a service';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const sendEmail = (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);

        emailjs.sendForm(
            'service_v1ui67x',
            'template_45lwovt',
            formRef.current,
            'jzz5LjP1wmDfDOqvu'
        ).then(
            () => {
                setLoading(false);
                setShowSuccessPopup(true);
                formRef.current.reset();
                setFormValues({
                    full_name: '',
                    email: '',
                    phone: '',
                    company: '',
                    service: '',
                    requirements: ''
                });
                setTimeout(() => setShowSuccessPopup(false), 3000);
            },
            (error) => {
                alert('Failed to send quote: ' + error.text);
                setLoading(false);
            }
        );
    };

    return (
        <>
            <Helmet>
                <title>Request a Transport Quote | PVR CABS Hyderabad</title>
                <meta name="description" content="Get a quote for employee transportation or corporate car services in Hyderabad. Trusted by 200+ businesses since 2007." />
                <link rel="canonical" href="https://pvrcabs.com/quote" />
            </Helmet>
            <div className="h-full mt-4 md:mt-6 px-2 md:px-0">
                {/* Success Popup */}
                {showSuccessPopup && (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-[9999]">
                        <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
                            <div className="flex flex-col items-center text-center">
                                <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                                <h3 className="text-xl font-bold text-gray-900 mb-2">Request Submitted Successfully!</h3>
                                <p className="text-gray-600 mb-4">We've received your quote request and will get back to you within 24 hours.</p>
                                <button
                                    onClick={() => setShowSuccessPopup(false)}
                                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Hero Section */}
                <div className="relative">
                    <img
                        src="/quotation.jpg"
                        className="w-full h-[180px] md:h-[250px] lg:h-[300px] object-cover"
                        alt="Quotation"
                    />
                    <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-white text-center px-4">
                        <h1 className="text-xl md:text-2xl lg:text-3xl font-bold mb-1 md:mb-2">
                            Get a <span className="text-blue-400">Quote</span>
                        </h1>
                        <p className="text-xs md:text-sm lg:text-base max-w-2xl mx-auto">
                            Experience reliable, safe, and personalized transportation services with PVR Cabs.
                            Serving Hyderabad since 2007 with over 300+ vehicles and professional drivers.
                        </p>
                    </div>
                </div>

                {/* Main Content */}
                <div className="max-w-7xl mx-auto px-0 md:px-4 py-0 md:py-10 bg-gray-200/0 md:bg-gray-800  mt-4">
                    <div className="grid lg:grid-cols-3 gap-8">
                        {/* Left Sidebar */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 lg:col-span-1 space-y-0 gap-4">
                            {/* Services Card */}
                            <div className="bg-white rounded-xl overflow-hidden border-2">
                                <div className="bg-gray-200 px-6 py-4">
                                    <h3 className="text-black font-bold text-base md:text-lg lg:text-xl">Our Services</h3>
                                </div>
                                <div className="p-4 md:p-6 space-y-0 md:space-y-3">
                                    <div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                                        <div>
                                            <h4 className="text-sm md:text-sm lg:text-base font-medium text-gray-900">Employee Transportation</h4>
                                            <p className="text-xs text-gray-600">IT & Corporate solutions</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                                        <div>
                                            <h4 className="text-sm md:text-sm lg:text-base font-medium text-gray-900">Corporate Car Rental</h4>
                                            <p className="text-xs text-gray-600">Professional fleet rental</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                                        <div>
                                            <h4 className="text-sm md:text-sm lg:text-base font-medium text-gray-900">Airport Transfer</h4>
                                            <p className="text-xs text-gray-600">Reliable pickup & drop</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                                        <div>
                                            <h4 className="text-sm md:text-sm lg:text-base font-medium text-gray-900">Monthly Rental</h4>
                                            <p className="text-xs text-gray-600">Long-term solutions</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Why Choose Us Card */}
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <h2 className="text-base md:text-lg lg:text-xl font-semibold text-gray-900 mb-6 flex items-center">
                                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                                    Why Choose PVR Cabs?
                                </h2>
                                <div className="space-y-4">
                                    <div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                                        <div className="p-2 rounded-lg bg-blue-50">
                                            <Car className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm md:text-base lg:text-lg font-semibold text-gray-800">600+ Fleet</h3>
                                            <p className="text-xs md:text-sm text-gray-600">Modern vehicles from sedans to buses</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                                        <div className="p-2 rounded-lg bg-emerald-50">
                                            <Clock className="w-5 h-5 text-emerald-600" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm md:text-base lg:text-lg font-semibold text-gray-800">Since 2007</h3>
                                            <p className="text-xs md:text-sm text-gray-600">17+ years of reliable service</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                                        <div className="p-2 rounded-lg bg-purple-50">
                                            <Users className="w-5 h-5 text-purple-600" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm md:text-base lg:text-lg font-semibold text-gray-800">Trained Drivers</h3>
                                            <p className="text-xs md:text-sm text-gray-600">Professional & safety-focused</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                                        <div className="p-2 rounded-lg bg-orange-50">
                                            <Shield className="w-5 h-5 text-orange-600" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm md:text-base lg:text-lg font-semibold text-gray-800">Safety First</h3>
                                            <p className="text-xs md:text-sm text-gray-600">Regular checks & monitoring</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Form Section */}
                        <div className="lg:col-span-2">
                            <div className="bg-blue-950 md:bg-blue-950 lg:bg-white text-white lg:text-gray-900 rounded-md p-8">
                                <div className="mb-8">
                                    <h2 className="text-base md:text-lg lg:text-xl font-extrabold mb-2">Request Your Quote</h2>
                                    <p className="text-xs md:text-sm text-slate-300 lg:text-gray-600">
                                        Fill out the form below and we'll get back to you within 24 hours
                                    </p>
                                </div>

                                <form ref={formRef} onSubmit={sendEmail} className="space-y-6">
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Full Name *</label>
                                            <input
                                                type="text"
                                                name="full_name"
                                                value={formValues.full_name}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-2 border ${errors.full_name ? 'border-red-500' : 'border-gray-300'} rounded-md text-gray-900`}
                                                placeholder="Your full name"
                                            />
                                            {errors.full_name && <p className="text-red-500 text-sm mt-1">{errors.full_name}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Email Address *</label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formValues.email}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md text-gray-900`}
                                                placeholder="you@example.com"
                                            />
                                            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                                        </div>
                                    </div>

                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Phone Number *</label>
                                            <input
                                                type="text"
                                                name="phone"
                                                value={formValues.phone}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-2 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-md text-gray-900`}
                                                placeholder="+91 9876543210"
                                            />
                                            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Company Name</label>
                                            <input
                                                type="text"
                                                name="company"
                                                value={formValues.company}
                                                onChange={handleChange}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-900"
                                                placeholder="Optional"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Service Required *</label>
                                        <select
                                            name="service"
                                            value={formValues.service}
                                            onChange={handleChange}
                                            className={`w-full px-4 py-2 border ${errors.service ? 'border-red-500' : 'border-gray-300'} rounded-md text-gray-900`}
                                        >
                                            <option value="">Select a service</option>
                                            <option>Employee Transportation</option>
                                            <option>Corporate Car Rental</option>
                                            <option>Airport Transfer</option>
                                            <option>Monthly Rental</option>
                                        </select>
                                        {errors.service && <p className="text-red-500 text-sm mt-1">{errors.service}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-1">Additional Requirements</label>
                                        <textarea
                                            name="requirements"
                                            rows="4"
                                            value={formValues.requirements}
                                            onChange={handleChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md resize-none text-gray-900"
                                            placeholder="Tell us about your route, passenger count, timings etc."
                                        ></textarea>
                                    </div>

                                    <div>
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full flex justify-center items-center space-x-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            {loading ? (
                                                <>
                                                    <svg
                                                        className="animate-spin h-5 w-5 mr-2 text-white"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                        ></circle>
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                        ></path>
                                                    </svg>
                                                    <span>Processing...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Send className="w-4 h-4" />
                                                    <span>Get Your Quote</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
}